import React, { useState, useEffect, useCallback } from 'react'
import { RotateCcw, Search, AlertTriangle, CheckCircle, XCircle, RefreshCw } from 'lucide-react'
import PageIdentifier from '../shared/PageIdentifier'
import SCREEN_IDS from '../../utils/screenIds'
import usePermissions from '../../hooks/usePermissions'

const blurOnEnter = (e) => { if (e.key === 'Enter') e.target.blur() }

const CURRENCIES = ['INR', 'USD']
const CONDITIONS = ['Good', 'Damaged', 'Needs Repair']

const EMPTY_FORM = {
  bill_number: '',
  customer_name: '',
  customer_phone: '',
  item_code: '',
  item_name: '',
  jewelry_category: '',
  gold_purity: '',
  gross_weight: '',
  net_weight: '',
  original_sale_price: '',
  refund_amount: '',
  currency: 'INR',
  return_reason: '',
  condition_on_return: 'Good',
  restock_item: true,
}

const JewelryReturns = () => {
  const { hasPermission } = usePermissions()
  const canCreate = hasPermission('jewelry-returns', 'create')
  const [tab, setTab] = useState('new')   // 'new' | 'history'

  // ── Form state ──────────────────────────────────────────────────────────────
  const [form, setForm] = useState(EMPTY_FORM)
  const [billLookupStatus, setBillLookupStatus] = useState(null) // null | 'loading' | 'found' | 'not_found'
  const [isSaving, setIsSaving] = useState(false)
  const [saveResult, setSaveResult] = useState(null) // { success, message, returnNumber }

  // ── Bill search state ─────────────────────────────────────────────────────────
  const [showBillSearch, setShowBillSearch] = useState(false)
  const [billSearchTerm, setBillSearchTerm] = useState('')
  const [billSearchResults, setBillSearchResults] = useState([])
  const [billSearchLoading, setBillSearchLoading] = useState(false)

  // ── History state ────────────────────────────────────────────────────────────
  const [returns, setReturns] = useState([])
  const [historyLoading, setHistoryLoading] = useState(false)
  const [filters, setFilters] = useState({ search: '', currency: '', status: 'completed' })
  const [cancelModal, setCancelModal] = useState(null) // { id, returnNumber }
  const [cancelReason, setCancelReason] = useState('')
  const [cancelling, setCancelling] = useState(false)

  // ── Helpers ──────────────────────────────────────────────────────────────────
  const set = (field) => (e) => {
    const val = e.target.type === 'checkbox' ? e.target.checked : e.target.value
    setForm((prev) => ({ ...prev, [field]: val }))
  }

  // ── Bill lookup ──────────────────────────────────────────────────────────────
  const lookupBill = useCallback(async () => {
    const bn = form.bill_number.trim()
    if (!bn) return
    setBillLookupStatus('loading')
    try {
      const res = await fetch(`/api/billings/bill/${encodeURIComponent(bn)}`, { credentials: 'include' })
      const data = await res.json()
      if (data.success && data.data) {
        const b = data.data
        setForm((prev) => ({
          ...prev,
          customer_name:       b.customer_name  || prev.customer_name,
          customer_phone:      b.customer_phone || prev.customer_phone,
          item_code:           b.item_code      || prev.item_code,
          item_name:           b.item_name      || prev.item_name,
          jewelry_category:    b.jewelry_category || prev.jewelry_category,
          gold_purity:         b.gold_purity    || prev.gold_purity,
          gross_weight:        b.gross_weight   != null ? String(b.gross_weight) : prev.gross_weight,
          net_weight:          b.net_weight     != null ? String(b.net_weight)   : prev.net_weight,
          original_sale_price: b.grand_total    != null ? String(b.grand_total)  : prev.original_sale_price,
          refund_amount:       b.grand_total    != null ? String(b.grand_total)  : prev.refund_amount,
          currency:            b.currency       || prev.currency,
        }))
        setBillLookupStatus('found')
      } else {
        setBillLookupStatus('not_found')
      }
    } catch {
      setBillLookupStatus('not_found')
    }
  }, [form.bill_number])

  // ── Bill search ──────────────────────────────────────────────────────────────
  const searchBills = useCallback(async () => {
    const term = billSearchTerm.trim()
    if (!term) return
    setBillSearchLoading(true)
    setBillSearchResults([])
    try {
      const res = await fetch(
        `/api/billings?search=${encodeURIComponent(term)}&status=completed&limit=10`,
        { credentials: 'include' }
      )
      const data = await res.json()
      setBillSearchResults(Array.isArray(data.data) ? data.data : [])
    } catch {
      setBillSearchResults([])
    } finally {
      setBillSearchLoading(false)
    }
  }, [billSearchTerm])

  const selectBillFromSearch = (bill) => {
    setForm((prev) => ({
      ...prev,
      bill_number:         bill.bill_number,
      customer_name:       bill.customer_name  || prev.customer_name,
      customer_phone:      bill.customer_phone || prev.customer_phone,
      item_code:           bill.item_code      || prev.item_code,
      item_name:           bill.item_name      || prev.item_name,
      jewelry_category:    bill.jewelry_category || prev.jewelry_category,
      gold_purity:         bill.gold_purity    || prev.gold_purity,
      gross_weight:        bill.gross_weight   != null ? String(bill.gross_weight) : prev.gross_weight,
      net_weight:          bill.net_weight     != null ? String(bill.net_weight)   : prev.net_weight,
      original_sale_price: bill.grand_total    != null ? String(bill.grand_total)  : prev.original_sale_price,
      refund_amount:       bill.grand_total    != null ? String(bill.grand_total)  : prev.refund_amount,
      currency:            bill.currency       || prev.currency,
    }))
    setBillLookupStatus('found')
    setShowBillSearch(false)
    setBillSearchTerm('')
    setBillSearchResults([])
  }

  // ── Submit ───────────────────────────────────────────────────────────────────
  const handleSubmit = async (e) => {
    e.preventDefault()
    if (!canCreate) {
      setSaveResult({ success: false, message: 'You do not have permission to record returns' })
      return
    }
    setSaveResult(null)
    setIsSaving(true)
    try {
      const payload = {
        ...form,
        gross_weight:        form.gross_weight        ? parseFloat(form.gross_weight)        : undefined,
        net_weight:          form.net_weight          ? parseFloat(form.net_weight)          : undefined,
        original_sale_price: form.original_sale_price ? parseFloat(form.original_sale_price) : undefined,
        refund_amount:       parseFloat(form.refund_amount),
      }
      const res = await fetch('/api/returns', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(payload),
      })
      const data = await res.json()
      if (data.success) {
        setSaveResult({ success: true, message: 'Return recorded successfully!', returnNumber: data.data?.return_number })
        setForm(EMPTY_FORM)
        setBillLookupStatus(null)
      } else {
        setSaveResult({ success: false, message: data.error || (data.details?.join(', ') ?? 'Failed to save return') })
      }
    } catch {
      setSaveResult({ success: false, message: 'Network error. Please try again.' })
    } finally {
      setIsSaving(false)
    }
  }

  // ── Fetch history ─────────────────────────────────────────────────────────────
  const fetchHistory = useCallback(async () => {
    setHistoryLoading(true)
    try {
      const params = new URLSearchParams()
      if (filters.search)   params.set('search', filters.search)
      if (filters.currency) params.set('currency', filters.currency)
      if (filters.status)   params.set('status', filters.status)
      const res = await fetch(`/api/returns?${params}`, { credentials: 'include' })
      const data = await res.json()
      if (data.success) setReturns(data.data)
    } catch {
      // ignore
    } finally {
      setHistoryLoading(false)
    }
  }, [filters])

  useEffect(() => {
    if (tab === 'history') fetchHistory()
  }, [tab, fetchHistory])

  // ── Cancel ────────────────────────────────────────────────────────────────────
  const handleCancel = async () => {
    if (!cancelModal) return
    setCancelling(true)
    try {
      const res = await fetch(`/api/returns/${cancelModal.id}/cancel`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ reason: cancelReason }),
      })
      const data = await res.json()
      if (data.success) {
        setCancelModal(null)
        setCancelReason('')
        fetchHistory()
      } else {
        alert(data.error || 'Failed to cancel return')
      }
    } catch {
      alert('Network error')
    } finally {
      setCancelling(false)
    }
  }

  const currencySymbol = form.currency === 'USD' ? '$' : '₹'

  return (
    <div className="p-6 max-w-5xl mx-auto">
      <PageIdentifier screenId={SCREEN_IDS.BILLING.RETURNS || 'RET-001'} />

      {/* Header */}
      <div className="flex items-center space-x-3 mb-6">
        <RotateCcw className="text-amber-500" size={28} />
        <div>
          <h1 className="text-2xl font-bold text-gray-800">Customer Returns</h1>
          <p className="text-sm text-gray-500">Record jewellery returned by a customer and process refund</p>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex border-b border-gray-200 mb-6">
        {[['new', 'New Return'], ['history', 'Return History']].map(([id, label]) => (
          <button
            key={id}
            onClick={() => { setTab(id); setSaveResult(null) }}
            className={`px-6 py-2 text-sm font-medium border-b-2 transition-colors ${
              tab === id
                ? 'border-amber-500 text-amber-600'
                : 'border-transparent text-gray-500 hover:text-gray-700'
            }`}
          >
            {label}
          </button>
        ))}
      </div>

      {/* ── NEW RETURN FORM ────────────────────────────────────────────────────── */}
      {tab === 'new' && (
        <form onSubmit={handleSubmit} className="space-y-6">

          {/* Save result banner */}
          {saveResult && (
            <div className={`flex items-start space-x-3 p-4 rounded-lg ${
              saveResult.success ? 'bg-green-50 border border-green-200' : 'bg-red-50 border border-red-200'
            }`}>
              {saveResult.success
                ? <CheckCircle className="text-green-500 mt-0.5 flex-shrink-0" size={20} />
                : <XCircle    className="text-red-500   mt-0.5 flex-shrink-0" size={20} />
              }
              <div>
                <p className={`font-medium ${saveResult.success ? 'text-green-800' : 'text-red-800'}`}>
                  {saveResult.message}
                </p>
                {saveResult.success && saveResult.returnNumber && (
                  <p className="text-sm text-green-700 mt-0.5">Return No: <strong>{saveResult.returnNumber}</strong></p>
                )}
              </div>
            </div>
          )}

          {/* ── Section 1: Bill Lookup ─────────────────────────────────────── */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-5">
            <h2 className="text-sm font-semibold text-gray-600 uppercase tracking-wide mb-4">
              1. Original Bill (Optional)
            </h2>
            <div className="flex space-x-3">
              <div className="flex-1">
                <label className="block text-xs font-medium text-gray-600 mb-1">Bill Number</label>
                <input
                  type="text"
                  value={form.bill_number}
                  onChange={set('bill_number')}
                  onKeyDown={blurOnEnter}
                  placeholder="e.g. BILL-INR-20260301-001"
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-amber-400"
                />
              </div>
              <div className="flex items-end">
                <button
                  type="button"
                  onClick={lookupBill}
                  disabled={!form.bill_number.trim() || billLookupStatus === 'loading'}
                  className="flex items-center space-x-2 px-4 py-2 bg-amber-500 text-white rounded-lg text-sm font-medium hover:bg-amber-600 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                >
                  <Search size={14} />
                  <span>{billLookupStatus === 'loading' ? 'Looking up…' : 'Lookup'}</span>
                </button>
              </div>
            </div>

            {billLookupStatus === 'found' && (
              <p className="mt-2 text-sm text-green-700 flex items-center space-x-1">
                <CheckCircle size={14} /> <span>Bill found — form pre-filled. Review and adjust if needed.</span>
              </p>
            )}
            {billLookupStatus === 'not_found' && (
              <p className="mt-2 text-sm text-red-600 flex items-center space-x-1">
                <AlertTriangle size={14} /> <span>Bill not found. Fill in the details manually.</span>
              </p>
            )}

            {/* ── Search by customer / item code ── */}
            <div className="mt-3 border-t border-gray-100 pt-3">
              <button
                type="button"
                onClick={() => { setShowBillSearch((v) => !v); setBillSearchResults([]) }}
                className="text-xs text-amber-600 hover:text-amber-700 font-medium underline underline-offset-2"
              >
                {showBillSearch ? 'Hide search' : "Don't know the bill number? Search by customer or item code"}
              </button>

              {showBillSearch && (
                <div className="mt-3 space-y-2">
                  <div className="flex space-x-2">
                    <input
                      type="text"
                      value={billSearchTerm}
                      onChange={(e) => setBillSearchTerm(e.target.value)}
                      onKeyDown={(e) => e.key === 'Enter' && (e.preventDefault(), searchBills())}
                      placeholder="Customer name, phone, or item code…"
                      className="flex-1 border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-amber-400"
                    />
                    <button
                      type="button"
                      onClick={searchBills}
                      disabled={!billSearchTerm.trim() || billSearchLoading}
                      className="flex items-center space-x-1 px-3 py-2 bg-gray-700 text-white rounded-lg text-sm font-medium hover:bg-gray-800 disabled:opacity-50 transition-colors"
                    >
                      <Search size={14} />
                      <span>{billSearchLoading ? 'Searching…' : 'Search'}</span>
                    </button>
                  </div>

                  {billSearchResults.length > 0 && (
                    <div className="border border-gray-200 rounded-lg overflow-hidden">
                      {billSearchResults.map((bill) => (
                        <button
                          key={bill.id}
                          type="button"
                          onClick={() => selectBillFromSearch(bill)}
                          className="w-full text-left px-4 py-3 hover:bg-amber-50 border-b border-gray-100 last:border-0 transition-colors"
                        >
                          <div className="flex items-center justify-between">
                            <div>
                              <span className="text-sm font-semibold text-gray-800">{bill.bill_number}</span>
                              <span className="ml-2 text-xs text-gray-500">{bill.customer_name}</span>
                              {bill.item_code && (
                                <span className="ml-2 text-xs bg-gray-100 text-gray-600 px-1.5 py-0.5 rounded">{bill.item_code}</span>
                              )}
                            </div>
                            <div className="text-right">
                              <span className="text-sm font-medium text-gray-700">
                                {bill.currency === 'USD' ? '$' : '₹'}{parseFloat(bill.grand_total).toLocaleString()}
                              </span>
                              <p className="text-xs text-gray-400">{new Date(bill.created_at).toLocaleDateString()}</p>
                            </div>
                          </div>
                        </button>
                      ))}
                    </div>
                  )}

                  {!billSearchLoading && billSearchTerm && billSearchResults.length === 0 && (
                    <p className="text-xs text-gray-500 text-center py-2">No completed bills found for "{billSearchTerm}"</p>
                  )}
                </div>
              )}
            </div>
          </div>

          {/* ── Section 2: Customer ────────────────────────────────────────── */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-5">
            <h2 className="text-sm font-semibold text-gray-600 uppercase tracking-wide mb-4">2. Customer Details</h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">Customer Name <span className="text-red-500">*</span></label>
                <input
                  type="text"
                  required
                  value={form.customer_name}
                  onChange={set('customer_name')}
                  onKeyDown={blurOnEnter}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-amber-400"
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">Phone</label>
                <input
                  type="tel"
                  value={form.customer_phone}
                  onChange={set('customer_phone')}
                  onKeyDown={blurOnEnter}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-amber-400"
                />
              </div>
            </div>
          </div>

          {/* ── Section 3: Item ────────────────────────────────────────────── */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-5">
            <h2 className="text-sm font-semibold text-gray-600 uppercase tracking-wide mb-4">3. Item Details</h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">Item Code</label>
                <input
                  type="text"
                  value={form.item_code}
                  onChange={set('item_code')}
                  onKeyDown={blurOnEnter}
                  placeholder="e.g. RNG-001"
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-amber-400"
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">Item Name</label>
                <input
                  type="text"
                  value={form.item_name}
                  onChange={set('item_name')}
                  onKeyDown={blurOnEnter}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-amber-400"
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">Jewellery Category</label>
                <input
                  type="text"
                  value={form.jewelry_category}
                  onChange={set('jewelry_category')}
                  onKeyDown={blurOnEnter}
                  placeholder="e.g. Ring, Necklace"
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-amber-400"
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">Gold Purity</label>
                <select
                  value={form.gold_purity}
                  onChange={set('gold_purity')}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-amber-400"
                >
                  <option value="">— Select —</option>
                  {['24K', '22K', '18K', '14K', 'Silver', 'Platinum', 'Other'].map(p => (
                    <option key={p} value={p}>{p}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">Gross Weight (g)</label>
                <input
                  type="number" min="0" step="0.001"
                  value={form.gross_weight}
                  onChange={set('gross_weight')}
                  onKeyDown={blurOnEnter}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-amber-400"
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">Net Weight (g)</label>
                <input
                  type="number" min="0" step="0.001"
                  value={form.net_weight}
                  onChange={set('net_weight')}
                  onKeyDown={blurOnEnter}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-amber-400"
                />
              </div>
            </div>
          </div>

          {/* ── Section 4: Return & Refund ─────────────────────────────────── */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-5">
            <h2 className="text-sm font-semibold text-gray-600 uppercase tracking-wide mb-4">4. Return &amp; Refund</h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">Currency <span className="text-red-500">*</span></label>
                <select
                  required
                  value={form.currency}
                  onChange={set('currency')}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-amber-400"
                >
                  {CURRENCIES.map(c => <option key={c}>{c}</option>)}
                </select>
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">
                  Original Sale Price ({currencySymbol})
                </label>
                <input
                  type="number" min="0" step="0.01"
                  value={form.original_sale_price}
                  onChange={set('original_sale_price')}
                  onKeyDown={blurOnEnter}
                  placeholder="Auto-filled from bill"
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-amber-400"
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">
                  Refund Amount ({currencySymbol}) <span className="text-red-500">*</span>
                </label>
                <input
                  type="number" required min="0" step="0.01"
                  value={form.refund_amount}
                  onChange={set('refund_amount')}
                  onKeyDown={blurOnEnter}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-amber-400"
                />
                {form.original_sale_price && form.refund_amount &&
                  parseFloat(form.refund_amount) < parseFloat(form.original_sale_price) && (
                  <p className="text-xs text-amber-600 mt-1">
                    Partial refund — deducting {currencySymbol}
                    {(parseFloat(form.original_sale_price) - parseFloat(form.refund_amount)).toFixed(2)}
                  </p>
                )}
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">Item Condition</label>
                <select
                  value={form.condition_on_return}
                  onChange={set('condition_on_return')}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-amber-400"
                >
                  {CONDITIONS.map(c => <option key={c}>{c}</option>)}
                </select>
              </div>
              <div className="sm:col-span-2">
                <label className="block text-xs font-medium text-gray-600 mb-1">Return Reason</label>
                <textarea
                  rows={2}
                  value={form.return_reason}
                  onChange={set('return_reason')}
                  placeholder="Customer dissatisfied, size issue, defect, etc."
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-amber-400 resize-none"
                />
              </div>
            </div>

            {/* Restock toggle */}
            <div className="mt-4 flex items-center space-x-3 p-3 bg-amber-50 rounded-lg border border-amber-100">
              <input
                id="restock"
                type="checkbox"
                checked={form.restock_item}
                onChange={set('restock_item')}
                className="h-4 w-4 text-amber-500 rounded border-gray-300 focus:ring-amber-400"
              />
              <label htmlFor="restock" className="text-sm text-gray-700 cursor-pointer">
                <span className="font-medium">Restock item</span> — set item status back to <span className="font-medium text-green-700">In Stock</span> after return
              </label>
            </div>
            {!form.restock_item && (
              <p className="mt-1 text-xs text-gray-500 ml-7">
                Item will remain as <span className="font-medium">Sold</span> in inventory (use for damaged/non-returnable items).
              </p>
            )}
          </div>

          {/* Submit */}
          <div className="flex justify-end">
            {canCreate ? (
              <button
                type="submit"
                disabled={isSaving}
                className="flex items-center space-x-2 px-6 py-2.5 bg-amber-500 text-white rounded-lg font-medium hover:bg-amber-600 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                <RotateCcw size={16} />
                <span>{isSaving ? 'Saving…' : 'Record Return'}</span>
              </button>
            ) : (
              <p className="text-sm text-gray-500 italic">You do not have permission to record returns.</p>
            )}
          </div>
        </form>
      )}

      {/* ── HISTORY ─────────────────────────────────────────────────────────────── */}
      {tab === 'history' && (
        <div className="space-y-4">
          {/* Filters */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-4">
            <div className="flex flex-wrap gap-3">
              <div className="flex-1 min-w-48">
                <div className="relative">
                  <Search className="absolute left-3 top-2.5 text-gray-400" size={14} />
                  <input
                    type="text"
                    placeholder="Search by customer, item code, return/bill no…"
                    value={filters.search}
                    onChange={(e) => setFilters(f => ({ ...f, search: e.target.value }))}
                    className="pl-8 w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-amber-400"
                  />
                </div>
              </div>
              <select
                value={filters.currency}
                onChange={(e) => setFilters(f => ({ ...f, currency: e.target.value }))}
                className="border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-amber-400"
              >
                <option value="">All Currencies</option>
                <option>INR</option>
                <option>USD</option>
              </select>
              <select
                value={filters.status}
                onChange={(e) => setFilters(f => ({ ...f, status: e.target.value }))}
                className="border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-amber-400"
              >
                <option value="">All Statuses</option>
                <option value="completed">Completed</option>
                <option value="cancelled">Cancelled</option>
              </select>
              <button
                onClick={fetchHistory}
                className="flex items-center space-x-1 px-4 py-2 bg-gray-100 text-gray-700 rounded-lg text-sm hover:bg-gray-200 transition-colors"
              >
                <RefreshCw size={14} />
                <span>Refresh</span>
              </button>
            </div>
          </div>

          {/* Table */}
          {historyLoading ? (
            <div className="text-center py-12 text-gray-500">Loading returns…</div>
          ) : returns.length === 0 ? (
            <div className="text-center py-12 text-gray-500">
              <RotateCcw size={40} className="mx-auto mb-3 text-gray-300" />
              <p>No returns found</p>
            </div>
          ) : (
            <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="bg-gray-50 border-b border-gray-200">
                    <tr>
                      {['Return No', 'Bill No', 'Customer', 'Item Code', 'Refund', 'Condition', 'Restocked', 'Status', 'Date', 'Actions'].map(h => (
                        <th key={h} className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wide whitespace-nowrap">{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {returns.map((r) => (
                      <tr key={r.id} className="hover:bg-gray-50 transition-colors">
                        <td className="px-4 py-3 font-mono text-xs text-amber-700 whitespace-nowrap">{r.return_number}</td>
                        <td className="px-4 py-3 font-mono text-xs text-gray-600 whitespace-nowrap">{r.bill_number || '—'}</td>
                        <td className="px-4 py-3 whitespace-nowrap">
                          <div className="font-medium text-gray-900">{r.customer_name}</div>
                          {r.customer_phone && <div className="text-xs text-gray-500">{r.customer_phone}</div>}
                        </td>
                        <td className="px-4 py-3 font-mono text-xs whitespace-nowrap">{r.item_code || '—'}</td>
                        <td className="px-4 py-3 whitespace-nowrap font-medium text-gray-900">
                          {r.currency === 'USD' ? '$' : '₹'}{parseFloat(r.refund_amount).toLocaleString('en-IN', { maximumFractionDigits: 2 })}
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap">
                          <span className={`inline-flex px-2 py-0.5 rounded-full text-xs font-medium ${
                            r.condition_on_return === 'Good'         ? 'bg-green-100 text-green-800' :
                            r.condition_on_return === 'Damaged'      ? 'bg-red-100   text-red-800'   :
                                                                        'bg-yellow-100 text-yellow-800'
                          }`}>{r.condition_on_return}</span>
                        </td>
                        <td className="px-4 py-3 text-center">
                          {r.restock_item
                            ? <CheckCircle size={16} className="text-green-500 mx-auto" />
                            : <XCircle    size={16} className="text-gray-300  mx-auto" />
                          }
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap">
                          <span className={`inline-flex px-2 py-0.5 rounded-full text-xs font-medium ${
                            r.status === 'completed' ? 'bg-blue-100 text-blue-800' : 'bg-gray-100 text-gray-500'
                          }`}>{r.status}</span>
                        </td>
                        <td className="px-4 py-3 text-xs text-gray-500 whitespace-nowrap">
                          {new Date(r.created_at).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })}
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap">
                          {r.status === 'completed' && (
                            <button
                              onClick={() => setCancelModal({ id: r.id, returnNumber: r.return_number })}
                              className="text-xs text-red-600 hover:text-red-800 font-medium"
                            >
                              Cancel
                            </button>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>
      )}

      {/* ── Cancel Modal ─────────────────────────────────────────────────────────── */}
      {cancelModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-md p-6">
            <h3 className="text-lg font-semibold text-gray-800 mb-2">Cancel Return</h3>
            <p className="text-sm text-gray-600 mb-4">
              Cancel <span className="font-mono font-medium">{cancelModal.returnNumber}</span>?
              If the item was restocked, it will be set back to <strong>Sold</strong>.
            </p>
            <textarea
              rows={3}
              placeholder="Reason for cancellation…"
              value={cancelReason}
              onChange={(e) => setCancelReason(e.target.value)}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-red-400 resize-none mb-4"
            />
            <div className="flex justify-end space-x-3">
              <button
                onClick={() => { setCancelModal(null); setCancelReason('') }}
                className="px-4 py-2 text-sm text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
              >
                Keep
              </button>
              <button
                onClick={handleCancel}
                disabled={cancelling}
                className="px-4 py-2 text-sm text-white bg-red-500 rounded-lg hover:bg-red-600 disabled:opacity-50 transition-colors"
              >
                {cancelling ? 'Cancelling…' : 'Yes, Cancel Return'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

export default JewelryReturns
